//React
import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { formatDayLong, formatValue } from "../../../components/charts/formatters";
import { groupByTopic, freshness, headlineColumn, effectiveLatestDay } from "./topics";
import { regionFor } from "./ranges";
import { usePanelDataset } from "./usePanelDataset";
//CSS
import "./index.scss";

/**
 * The Admin Dashboards landing page (ASB-4377, design A.5): one section per
 * topic, in the API's order, and one card per panel linking to it. A card
 * carries the panel's title and description, the day its data runs through
 * (or that it has none, or that it has gone stale), its regions when there is
 * more than one, and — when the manifest declares a `headline` — that one
 * number, read from the panel's latest day.
 */

/** The headline number: the latest day's value of the named kpi column, formatted by its type. */
function Headline({ panel, dataset, column, className }) {
	const region = regionFor(panel, null);
	const latestDay = effectiveLatestDay(panel);
	const { status, data } = usePanelDataset(panel.id, dataset.id, {
		from: latestDay,
		to: latestDay,
		region,
		version: panel.computedAt,
	});
	// A failure or an empty answer leaves the card without a number; the panel
	// page says why, with a retry, if the admin opens it.
	if (status === "loading") {
		return (
			<span
				className={`${className} ${className}--loading`}
				aria-busy="true"
				aria-label={`${column.label} loading`}
			/>
		);
	}
	if (status !== "ready") return null;
	const value = data.rows[0]?.[column.key];
	if (value === null || value === undefined) return null;
	return (
		<span className={className}>
			<span className={`${className}-value`}>{formatValue(column.type, value, "axis")}</span>
			<span className={`${className}-label`}>{column.label}</span>
		</span>
	);
}

Headline.propTypes = {
	panel: PropTypes.object.isRequired,
	dataset: PropTypes.object.isRequired,
	column: PropTypes.object.isRequired,
	className: PropTypes.string.isRequired,
};

export function PanelSummaryCard({ panel, to, now }) {
	const state = freshness(panel.latestDay, now, panel.dataFrom);
	const headline = state === "none" ? null : headlineColumn(panel);
	const regions = Array.isArray(panel.regions) ? panel.regions : [];
	let data;
	if (state === "none") data = "No data yet";
	else if (state === "stale") data = `No data since ${formatDayLong(panel.latestDay)}`;
	else data = `Data through ${formatDayLong(panel.latestDay)}`;
	return (
		<Link
			to={to}
			className={`as-panel-card as-panel-summary as-panel-summary--${state}`}
			data-panel={panel.id}
			data-freshness={state}
		>
			<span className="as-panel-summary__heading">
				<span className="as-panel-summary__title">{panel.title ?? panel.id}</span>
				{panel.description ? (
					<span className="as-panel-summary__description">{panel.description}</span>
				) : null}
			</span>
			{headline ? (
				<Headline
					panel={panel}
					dataset={headline.dataset}
					column={headline.column}
					className="as-panel-summary__headline"
				/>
			) : null}
			<span className="as-panel-summary__meta">
				<span className="as-panel-summary__data">{data}</span>
				{regions.length > 1 ? (
					<span className="as-panel-summary__regions">
						{regions.map((r) => r.toUpperCase()).join(" · ")}
					</span>
				) : null}
			</span>
		</Link>
	);
}

PanelSummaryCard.propTypes = {
	/** a manifest from GET /admin/panels (A.2 + latestDay / computedAt) */
	panel: PropTypes.shape({
		id: PropTypes.string.isRequired,
		title: PropTypes.string,
		description: PropTypes.string,
		regions: PropTypes.arrayOf(PropTypes.string),
		latestDay: PropTypes.string,
		/** the first complete day of data (A.6); earlier days do not exist */
		dataFrom: PropTypes.string,
		headline: PropTypes.shape({ dataset: PropTypes.string, column: PropTypes.string }),
		datasets: PropTypes.array,
	}).isRequired,
	/** the router location of the panel page */
	to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
	/** "today" for the freshness rule; tests pin it */
	now: PropTypes.instanceOf(Date),
};

function Landing({ panels, panelPath, now }) {
	const topics = groupByTopic(panels);
	return (
		<div className="as-dashboard-topics">
			{topics.map((topic) => (
				<section
					key={topic.id}
					className="as-dashboard-topic"
					data-topic={topic.id}
					aria-labelledby={`as-dashboard-topic-${topic.id}`}
				>
					<h2
						className="as-dashboard-topic__title"
						id={`as-dashboard-topic-${topic.id}`}
					>
						{topic.title}
					</h2>
					{topic.description ? (
						<p className="as-dashboard-topic__description">{topic.description}</p>
					) : null}
					<div className="as-dashboard-topic__panels">
						{topic.panels.map((panel) => (
							<PanelSummaryCard
								key={panel.id}
								panel={panel}
								to={panelPath(panel.id)}
								now={now}
							/>
						))}
					</div>
				</section>
			))}
		</div>
	);
}

Landing.propTypes = {
	panels: PropTypes.arrayOf(PropTypes.object).isRequired,
	/** (panelId) => router location, keeping the page's query string */
	panelPath: PropTypes.func.isRequired,
	now: PropTypes.instanceOf(Date),
};

export default Landing;
